import {useMutation} from '@tanstack/react-query'
import {useConfirmOrderApiMethod} from 'api/useConfirmOrderApiMethod'
import {get} from 'lodash'
import {usePaymentPath, useSiteUrl} from 'providers/EnvironmentProvider'
import {Params} from 'types'
import addTrackingError, {addTrackingException} from 'utils/addTrackingError'
import {delay} from 'utils/delay'

const MAX_TOO_MANY_REQUESTS = 3
const MAX_PAYMENT_PENDING = 10
const DEFAULT_DELAY = 1

// Assessment failures that must never be retried automatically: the assessment failed
// for a non-retriable reason, or the order has exhausted its permitted refusals.
const NON_RETRIABLE_ERROR_CODES = ['VERIFICATION_FAILED', 'PAYMENT_DECLINE_LIMIT_EXCEEDED']

export const useConfirmOrderMutation = () => {
  const siteUrl = useSiteUrl()
  const paymentPath = usePaymentPath()
  const confirmOrder = useConfirmOrderApiMethod()

  return useMutation({
    mutationFn: async ({order, orderIntent}: Params.ConfirmOrder) => {
      let retriesTooManyRequests = 1
      let retriesPaymentPending = 1

      while (true) {
        try {
          // Each pass mints a fresh `purchase` token inside useConfirmOrderApiMethod,
          // so a repeated confirm never reuses the previous attempt's token.
          return await confirmOrder({order, orderIntent})
        } catch (error: any) {
          const errorCode = get(error, 'response.data.errorCode')

          if (NON_RETRIABLE_ERROR_CODES.includes(errorCode)) {
            addTrackingError(`Ventrata: ${errorCode}`, {
              level: 'error',
              action: 'confirmOrder',
              order: order.id,
            })

            throw error
          }

          if (errorCode === 'PAYMENT_PENDING') {
            // If Ventrata has not yet received a webhook from the payment gateway:
            addTrackingError('Ventrata: PAYMENT_PENDING', {
              level: 'info',
              action: 'confirmOrder',
              order: order.id,
              retries: retriesPaymentPending,
            })

            if (retriesPaymentPending >= MAX_PAYMENT_PENDING) {
              addTrackingException(error, {action: 'confirmOrder', order: order.id})
              throw error
            }

            retriesPaymentPending++
            retriesTooManyRequests = 1

            await delay((DEFAULT_DELAY + 0.5 * retriesPaymentPending) * 1000)
            continue
          } else if (get(error, 'response.data.error') === 'TOO_MANY_REQUESTS') {
            // If Ventrata is just failing to process the request:
            addTrackingError('Ventrata: TOO_MANY_REQUESTS', {
              level: 'error',
              action: 'confirmOrder',
              order: order.id,
              retries: retriesTooManyRequests,
              retryAfter: get(error, 'response.data.retryAfter'),
            })

            if (retriesTooManyRequests >= MAX_TOO_MANY_REQUESTS) {
              addTrackingException(error, {action: 'confirmOrder', order: order.id})
              throw error
            }

            retriesTooManyRequests++

            let thisDelay = DEFAULT_DELAY + 0.5 * retriesTooManyRequests
            let retryAfter = get(error, 'response.data.retryAfter')

            if (retryAfter) {
              thisDelay = retryAfter
            }

            await delay(thisDelay * 1000)
            continue
          } else if (errorCode === 'ALREADY_CONFIRMED') {
            // If Ventrata says the order has already been confirmed:
            addTrackingError('Ventrata: ALREADY_CONFIRMED', {
              level: 'info',
              action: 'confirmOrder',
              order: order.id,
              retries: retriesTooManyRequests,
            })

            return order
          } else if (errorCode === 'PAYMENT_DECLINED') {
            // If Ventrata says the payment was declined:
            addTrackingError('Ventrata: PAYMENT_DECLINED', {
              level: 'info',
              action: 'confirmOrder',
              order: order.id,
            })

            window.location.href = `${siteUrl}${paymentPath}`
          } else {
            addTrackingException(error, {action: 'confirmOrder', order: order.id})
            throw error
          }
        }
      }
    },
  })
}
